import {
  DefaultApi,
  TransactionFormatTransactionShapeEnum,
  Update,
  UpdateOneOf3,
} from '@digitalasset/json-api-ts-client/index';
import { useConfig } from '@hooks/other/useConfig';
import React, { useState, useCallback, useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useErrorContext } from '../../contexts/ErrorContext';
import { useLedgerApiClient } from '../../contexts/LedgerApiContext';
import { useParties } from '../../contexts/PartiesContext';

interface CCExplorerLinkProps {
  contractId: string;
}

const domainMapping: Record<string, string> = {
  testnet: 'testnet.',
  mainnet: '',
  devnet: 'devnet.',
};

const isTransactionUpdate = (update: Update): update is UpdateOneOf3 => {
  return update !== null && typeof update === 'object' && 'Transaction' in update;
};
export const CCExplorerLink: React.FC<CCExplorerLinkProps> = ({ contractId }) => {
  const [loading, setLoading] = useState(false);
  const config = useConfig();
  const { environment } = config.app;

  const [url, setUrl] = useState<string | null>(null);
  const ledgerApi = useLedgerApiClient();
  const { party } = useParties();
  const errorContext = useErrorContext();

  const generateLink = useCallback(async () => {
    if (!environment) {
      throw new Error('No environment set');
    }
    if (loading || url) return;

    setLoading(true);
    try {
      const api = ledgerApi.api() as DefaultApi;

      // 1. Extract offset
      const eventsResponse = await api.postV2EventsEventsByContractId({
        contractId,
        eventFormat: {
          filtersByParty: { [party]: { cumulative: [] } },
          verbose: false,
        },
      });

      const createdEvent = eventsResponse.created?.createdEvent;
      if (!createdEvent?.offset) throw new Error('No offset found');

      // 2. Extract updateId and recordTime
      const updateResponse = await api.postV2UpdatesUpdateByOffset({
        offset: createdEvent.offset,
        updateFormat: {
          includeTransactions: {
            transactionShape:
              TransactionFormatTransactionShapeEnum.TRANSACTION_SHAPE_LEDGER_EFFECTS,
            eventFormat: {
              filtersByParty: { [party]: { cumulative: [] } },
              verbose: false,
            },
          },
        },
      });

      if (!isTransactionUpdate(updateResponse.update)) {
        throw new Error('Transaction details missing');
      }

      const transaction = updateResponse.update.Transaction?.value;
      if (!transaction) throw new Error('Transaction details missing');

      const { updateId, recordTime } = transaction;
      const domain = domainMapping[environment];
      const generatedUrl = `https://${domain}ccexplorer.io/updates/${updateId}/${recordTime}?action=proofOfTransfer`;

      setUrl(generatedUrl);
    } catch (error) {
      console.error('Failed to generate explorer link:', error);
      errorContext.enqueueError(new Error('Failed to generate', { cause: error as Error }));
    } finally {
      setLoading(false);
    }
  }, [contractId, environment, ledgerApi, party, loading, url, errorContext]);

  const handleAction = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      generateLink();
    }
  };

  const label = useMemo(() => {
    if (loading) return 'Generating link...';
    if (url) return 'Click to open explorer';
    return 'Verify on explorer';
  }, [loading, url]);
  if (!environment) {
    return null;
  }
  if (environment === 'local') {
    return (
      <Typography color="warning" sx={{ textWrap: 'wrap' }} variant="caption">
        CC Explore only works on Devnet / Testnet / Mainnet.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        variant="caption"
        component="span"
        onClick={handleAction}
        sx={{
          cursor: 'pointer',
          textDecoration: url ? 'underline' : 'none',
          color: url ? 'primary.main' : 'text.secondary',
          '&:hover': { opacity: 0.8 },
        }}
      >
        {label}
      </Typography>
      {loading && <CircularProgress size={14} />}
    </Box>
  );
};
